import type { FC } from 'react';
import type { FragmentType } from '@packages/gql/generated/shopping';
import { unmask } from '@packages/gql/src/betterMasking';
import type { CategoryFilterNavigationMobileWorkaroundFragmentFragmentDoc } from '@packages/gql/generated/shopping/CategoryFilterNavigationMobileWorkaroundFragmentFragmentDoc';
import type { CategoryFilterNavigationMobileFragmentFragmentDoc } from '@packages/gql/generated/shopping/CategoryFilterNavigationMobileFragmentFragmentDoc';
import { useAbTesting } from '@packages/shared/src/abtesting/useAbTesting/useAbTesting';

import { CategoryFilterStandalone } from '../CategoryFilterStandalone/CategoryFilterStandalone';
import { useCategoryDataForFilterNavigationMobile } from './useCategoryDataForFilterNavigationMobile';
import { filterSettingsFab } from '../../activeFeatureFlags';
import { getTestId } from '../../utils/featureFlags/getTestId';

/* GraphQL */ `
  fragment CategoryFilterNavigationMobileFragment on FlatCategoryItem {
    ...UseCategoryDataForFilterNavigationMobileFragment
  }
`;

/* GraphQL */ `
  fragment CategoryFilterNavigationMobileWorkaroundFragment on CategoryResult {
    ...UseCategoryDataForFilterNavigationMobileWorkaroundFragment
  }
`;

export type CategoryFilterNavigationMobileProps = {
  /** Category items for list navigation */
  maskedItems: Array<FragmentType<typeof CategoryFilterNavigationMobileFragmentFragmentDoc>>;
  /** Root category for upwards navigation */
  maskedResult: FragmentType<typeof CategoryFilterNavigationMobileWorkaroundFragmentFragmentDoc>;
  /** Callback invoked when a category is selected */
  onNavigate?: () => void;
};

/**
 * Displays a single level of categories as navigation items, including upwards if possible.
 * */
export const CategoryFilterNavigationMobile: FC<CategoryFilterNavigationMobileProps> = ({
  maskedItems,
  maskedResult,
  onNavigate,
}) => {
  const { setOutcome } = useAbTesting();

  const items = maskedItems.map((item) =>
    unmask<typeof CategoryFilterNavigationMobileFragmentFragmentDoc>(item),
  );
  const result =
    unmask<typeof CategoryFilterNavigationMobileWorkaroundFragmentFragmentDoc>(maskedResult);

  const categories = useCategoryDataForFilterNavigationMobile(items, result);
  const onNavigateHandler = () => {
    if (onNavigate) {
      onNavigate();
    }

    // remove if A/B test is finished
    setOutcome(getTestId(filterSettingsFab), {
      FILTER: 1,
    });
  };

  return <CategoryFilterStandalone categories={categories} onNavigate={onNavigateHandler} />;
};
